import React, { useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { runJob } from "@/lib/jobs";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const rowStyle = { ok: "bg-emerald-50 text-emerald-700", error: "bg-red-50 text-red-700", skipped: "bg-amber-50 text-amber-700" };

const downloadTemplate = async () => {
  try {
    const r = await api.get("/attendance/dtr-import/template", { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = u; a.download = "DTR_Template.xlsx"; a.click(); URL.revokeObjectURL(u);
  } catch { toast.error("Template download failed"); }
};

const PreviewTable = ({ rows }) => (
  <div className="max-h-72 overflow-auto border border-slate-200 rounded-lg" data-testid="dtr-import-preview">
    <table className="w-full text-xs">
      <thead className="bg-slate-50 sticky top-0"><tr className="text-left text-slate-500">
        {["Row", "Employee No", "Name", "Date", "In", "Out", "Remarks", "Result"].map((h) => <th key={h} className="px-2 py-1.5 font-medium">{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.row} className="border-t border-slate-100" data-testid={`dtr-import-row-${r.row}`}>
            <td className="px-2 py-1.5 text-slate-400">{r.row}</td>
            <td className="px-2 py-1.5 font-mono">{r.employee_no || "—"}</td>
            <td className="px-2 py-1.5">{r.name || "—"}</td>
            <td className="px-2 py-1.5">{r.date || "—"}</td>
            <td className="px-2 py-1.5 font-mono">{r.time_in || "—"}</td>
            <td className="px-2 py-1.5 font-mono">{r.time_out || "—"}</td>
            <td className="px-2 py-1.5" data-testid={`dtr-import-remarks-${r.row}`}>{r.remarks || "—"}</td>
            <td className="px-2 py-1.5"><Badge variant="outline" className={`${rowStyle[r.status]} border-0 text-[10px] whitespace-normal text-left`} data-testid={`dtr-import-result-${r.row}`}>{r.message}</Badge></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const DtrImportDialog = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState(null);
  const run = useRef(0);

  const start = (commit) => {
    const fd = new FormData(); fd.append("file", file);
    return () => api.post(`/attendance/dtr-import?commit=${commit}&background=true`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  };
  const preview_ = async () => {
    if (!file) return;
    const me = ++run.current;
    setBusy(true); setProg(0);
    const live = () => me === run.current;
    try {
      const data = await runJob(start(false), { silent: true, onProgress: (j) => live() && setProg(j.total ? Math.round((j.done / j.total) * 100) : 0) });
      if (live()) setPreview(data);
    } catch { /* toast shown by runJob */ }
    if (live()) { setBusy(false); setProg(null); }
  };
  const commit = () => {
    runJob(start(true)).then((data) => {
      toast.success(`Imported ${data.counts.ok} row(s), skipped ${data.counts.skipped}, errors ${data.counts.error}`); onImported();
    }).catch(() => {});
    toast.info("Import is running in the background — you can keep working.");
    close(false);
  };
  const close = (v) => { if (!v) { run.current += 1; setFile(null); setPreview(null); setBusy(false); setProg(null); } onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl" data-testid="dtr-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Import DTR</DialogTitle>
          <DialogDescription>Upload the completed DTR Template (.xlsx). Existing unverified rows for the same date are overwritten; verified rows are locked and skipped. Remarks and Approved Overtime create approved Leave, OB, WFH, DTR Correction and Overtime forms and update leave balances.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="dtr-template-download"><Icons.FileSpreadsheet className="h-4 w-4 mr-1.5" />Download DTR Template</Button>
          <span className="text-xs text-slate-500 self-center">Fill in User ID, Date, Time In, Time Out, Approved Overtime and Remarks (e.g. VL With-Pay, VL Halfday Without-Pay, OB, WFH, DTR Correction, BL With-Pay, Absent). See the Remarks Guide sheet.</span>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="dtr-import-file" />
          <Button onClick={preview_} disabled={!file || busy} data-testid="dtr-import-preview-btn">{busy ? "Reading…" : "Preview"}</Button>
        </div>
        {prog !== null && (
          <div className="flex items-center gap-2" data-testid="dtr-import-progress"><Progress value={prog} className="h-2 flex-1" /><span className="text-xs text-slate-500 tabular-nums w-10 text-right">{prog}%</span></div>
        )}
        {preview && (
          <>
            <div className="flex gap-2 text-xs" data-testid="dtr-import-counts">
              <Badge className={rowStyle.ok}>{preview.counts.ok} ready</Badge>
              <Badge className={rowStyle.skipped}>{preview.counts.skipped} skipped</Badge>
              <Badge className={rowStyle.error}>{preview.counts.error} error{preview.counts.error === 1 ? "" : "s"}</Badge>
            </div>
            <PreviewTable rows={preview.rows} />
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid="dtr-import-cancel">Cancel</Button>
          <Button onClick={commit} disabled={!preview || !preview.counts.ok || busy} data-testid="dtr-import-confirm">Confirm Import ({preview?.counts.ok || 0})</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
